import React from "react";
import { useNavigate } from "react-router-dom";
import { PhoneIncoming, PhoneMissed, PhoneCall, MessageSquare, Voicemail, AlertOctagon, Headphones } from "lucide-react";
import { Panel, InlineError, timeAgo, Overline } from "./singleUtils";

const Cell = ({ icon: Icon, label, value, tone, onClick, testid }) => (
  <button onClick={onClick} className="rounded-sm border border-border/60 bg-muted/20 px-2 py-1.5 text-left hover:border-amber-500/40 transition-colors" data-testid={testid}>
    <div className={`flex items-center gap-1 text-[10px] font-semibold ${tone}`}>
      <Icon className="w-3 h-3" /> {label}
    </div>
    <p className="text-base font-extrabold font-mono text-foreground leading-tight mt-0.5">{value}</p>
  </button>
);

const CommsPanel = ({ comms, onRetry }) => {
  const navigate = useNavigate();
  if (!comms) return null;
  if (comms.error) return <Panel title="Calls & Messages" icon={Headphones} testid="comms-panel"><InlineError label="communications" onRetry={onRetry} /></Panel>;
  return (
    <Panel title="Calls & Messages" icon={Headphones} testid="comms-panel">
      <div className="grid grid-cols-3 gap-1.5">
        <Cell icon={PhoneIncoming} label="Inbound" value={comms.inbound_calls ?? 0} tone="text-indigo-600 dark:text-indigo-400" onClick={() => navigate("/dashboard/calls")} testid="comms-inbound" />
        <Cell icon={PhoneCall} label="Answered" value={comms.answered_calls ?? 0} tone="text-emerald-600 dark:text-emerald-400" onClick={() => navigate("/dashboard/calls")} testid="comms-answered" />
        <Cell icon={PhoneMissed} label="Missed" value={comms.missed_calls ?? 0} tone="text-rose-600 dark:text-rose-500" onClick={() => navigate("/dashboard/calls?filter=missed")} testid="comms-missed" />
        <Cell icon={MessageSquare} label="Unread SMS" value={comms.unread_conversations ?? 0} tone="text-amber-600 dark:text-amber-400" onClick={() => navigate("/dashboard/messages?filter=unread")} testid="comms-unread" />
        <Cell icon={Voicemail} label="Voicemails" value={comms.voicemails_unread ?? 0} tone="text-violet-600 dark:text-violet-400" onClick={() => navigate("/dashboard/calls?tab=voicemails")} testid="comms-voicemails" />
        <Cell icon={AlertOctagon} label="Failed Msgs" value={comms.failed_messages ?? 0} tone="text-rose-600 dark:text-rose-500" onClick={() => navigate("/dashboard/messages")} testid="comms-failed" />
      </div>
      {(comms.recent || []).length > 0 && (
        <div className="mt-3">
          <Overline>Recent</Overline>
          <div className="mt-1 divide-y divide-border/40" data-testid="comms-recent">
            {(comms.recent || []).slice(0, 5).map((r, i) => (
              <button key={i} className="w-full text-left py-1.5 hover:bg-muted/30 rounded-sm px-1"
                onClick={() => navigate(r.type === "sms" ? "/dashboard/messages" : "/dashboard/calls")}
                data-testid={`comms-recent-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 min-w-0">
                    {r.type === "missed_call"
                      ? <PhoneMissed className="w-3 h-3 text-rose-500 shrink-0" />
                      : <MessageSquare className="w-3 h-3 text-indigo-500 shrink-0" />}
                    <span className="text-xs text-foreground truncate">{r.who || "Unknown"}</span>
                  </span>
                  <span className="text-[10px] text-muted-foreground font-mono shrink-0">{timeAgo(r.at)}</span>
                </div>
                {r.text && <p className="text-[11px] text-muted-foreground truncate mt-0.5 pl-[18px]">"{r.text}"</p>}
              </button>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
};

export default CommsPanel;
